import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';

export function CinemaScene() {
  const sceneRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [saveData, setSaveData] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const sceneX = useSpring(pointerX, { stiffness: 45, damping: 22 });
  const sceneY = useSpring(pointerY, { stiffness: 45, damping: 22 });
  const motionEnabled = !reducedMotion && !paused && !saveData;

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    setSaveData(connection?.saveData === true);
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !motionEnabled || !window.matchMedia('(pointer: fine)').matches) {
      pointerX.set(0);
      pointerY.set(0);
      return;
    }
    const parent = scene.parentElement;
    if (!parent) return;
    const onPointerMove = (event: PointerEvent) => {
      const bounds = parent.getBoundingClientRect();
      pointerX.set((event.clientX / bounds.width - .5) * -24);
      pointerY.set(((event.clientY - bounds.top) / bounds.height - .5) * -16);
    };
    const onPointerLeave = () => { pointerX.set(0); pointerY.set(0); };
    parent.addEventListener('pointermove', onPointerMove, { passive: true });
    parent.addEventListener('pointerleave', onPointerLeave);
    return () => { parent.removeEventListener('pointermove', onPointerMove); parent.removeEventListener('pointerleave', onPointerLeave); };
  }, [motionEnabled, pointerX, pointerY]);

  useEffect(() => {
    const video = videoRef.current;
    const scene = sceneRef.current;
    if (!video || !scene) return;
    let visible = true;
    const syncPlayback = () => {
      if (!motionEnabled || document.hidden || !visible) { video.pause(); return; }
      void video.play().catch(() => { setPlaying(false); setPlaybackError(true); });
    };
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; syncPlayback(); });
    observer.observe(scene);
    document.addEventListener('visibilitychange', syncPlayback);
    syncPlayback();
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', syncPlayback); video.pause(); };
  }, [motionEnabled]);

  const togglePlayback = () => {
    if (playing) { setPaused(true); videoRef.current?.pause(); return; }
    setPaused(false);
    const video = videoRef.current;
    if (!video) return;
    if (video.error) video.load();
    void video.play().catch(() => { setPlaying(false); setPlaybackError(true); });
  };
  const playbackLabel = playing ? 'Pause' : playbackError ? 'Retry' : 'Play';

  return <>
    <div ref={sceneRef} className="capital-cinema" aria-hidden="true">
      <motion.div className="capital-cinema-frame" style={{ x: sceneX, y: sceneY }}>
        <img src="/images/capital-in-motion.webp" alt="" fetchPriority="high" width={2048} height={1152} />
        {!saveData && !reducedMotion && <video ref={videoRef} muted loop playsInline preload="metadata" poster="/images/capital-in-motion.webp" onPlay={() => { setPlaying(true); setPlaybackError(false); }} onPause={() => setPlaying(false)} onError={() => { setPlaying(false); setPlaybackError(true); }}><source src="/videos/capital-in-motion.mp4" type="video/mp4" /></video>}
      </motion.div>
      <div className="capital-reticle capital-reticle-one" /><div className="capital-reticle capital-reticle-two" />
    </div>
    {!reducedMotion && !saveData && <button type="button" className="capital-film-control" onClick={togglePlayback} aria-label={`${playbackLabel} background film`}>{playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}<span>{playbackLabel.toUpperCase()} FILM</span></button>}
  </>;
}
